import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { userAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const UserDownline = () => {
  const { user } = useAuth();
  const [downlineData, setDownlineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [activeTeamType, setActiveTeamType] = useState('allTeam');

  // --- Monthly Business Tracker State ---
  const [trackerMonth, setTrackerMonth] = useState(() => {
    const istOffset = 5.5 * 60 * 60 * 1000;
    return new Date(Date.now() + istOffset).getUTCMonth() + 1;
  });
  const [trackerYear, setTrackerYear] = useState(() => {
    const istOffset = 5.5 * 60 * 60 * 1000;
    return new Date(Date.now() + istOffset).getUTCFullYear();
  });
  const [trackerType, setTrackerType] = useState('all'); // 'all', 'team', 'direct', 'downline', 'self'
  const [trackerData, setTrackerData] = useState(null);
  const [trackerLoading, setTrackerLoading] = useState(true);
  const [trackerError, setTrackerError] = useState('');
  const [dayViewFilter, setDayViewFilter] = useState('activeOnly'); // 'activeOnly' | 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTrackerDays, setExpandedTrackerDays] = useState(new Set());
  const [selectedChartDay, setSelectedChartDay] = useState(null);

  // Available years for dropdown: 2024 to currentYear + 1
  const availableYears = useMemo(() => {
    const currentYr = new Date().getFullYear();
    const years = [];
    for (let y = Math.min(2024, currentYr - 1); y <= currentYr + 1; y++) {
      years.push(y);
    }
    return years;
  }, []);

  // Fetch Downline Structure
  const fetchDownline = useCallback(async () => {
    if (!user) {
      setError('Please login to view your team');
      setLoading(false);
      return;
    }
    
    const userId = user._id || user.id;
    if (!userId) {
      setError('User ID not found');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const response = await userAPI.getMyDownline();
      
      if (response.data) {
        setDownlineData(response.data);
      } else {
        setError('No downline data received');
      }
    } catch (err) {
      console.error('Error fetching downline:', err);
      setError(err.response?.data?.error || 'Failed to load downline data');
      setDownlineData(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch Monthly Business Tracker Data
  const fetchMonthlyTracker = useCallback(async () => {
    if (!user) return;
    try {
      setTrackerLoading(true);
      setTrackerError('');
      const res = await userAPI.getMonthlyBusinessTracker({
        year: trackerYear,
        month: trackerMonth,
        type: trackerType
      });
      if (res.data?.success) {
        setTrackerData(res.data.data);
      } else {
        setTrackerError(res.data?.error || 'Failed to load tracker data');
      }
    } catch (err) {
      console.error('Error fetching monthly tracker:', err);
      setTrackerError(err.response?.data?.error || 'Failed to load monthly business tracker');
    } finally {
      setTrackerLoading(false);
    }
  }, [user, trackerYear, trackerMonth, trackerType]);

  useEffect(() => {
    fetchDownline();
  }, [fetchDownline]);

  useEffect(() => {
    fetchMonthlyTracker();
  }, [fetchMonthlyTracker]);

  // Navigation handlers
  const handlePrevMonth = () => {
    setSelectedChartDay(null);
    if (trackerMonth === 1) {
      setTrackerMonth(12);
      setTrackerYear(prev => prev - 1);
    } else {
      setTrackerMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    setSelectedChartDay(null);
    if (trackerMonth === 12) {
      setTrackerMonth(1);
      setTrackerYear(prev => prev + 1);
    } else {
      setTrackerMonth(prev => prev + 1);
    }
  };

  const handleCurrentMonth = () => {
    setSelectedChartDay(null);
    const istOffset = 5.5 * 60 * 60 * 1000;
    const nowIST = new Date(Date.now() + istOffset);
    setTrackerMonth(nowIST.getUTCMonth() + 1);
    setTrackerYear(nowIST.getUTCFullYear());
  };

  const toggleTrackerDay = (dayNum) => {
    setExpandedTrackerDays(prev => {
      const next = new Set(prev);
      if (next.has(dayNum)) {
        next.delete(dayNum);
      } else {
        next.add(dayNum);
      }
      return next;
    });
  };

  const toggleAllExpanded = (daysToToggle) => {
    const allExpanded = daysToToggle.every(d => expandedTrackerDays.has(d.day));
    if (allExpanded) {
      setExpandedTrackerDays(new Set());
    } else {
      setExpandedTrackerDays(new Set(daysToToggle.map(d => d.day)));
    }
  };

  // Filtered days list for the table
  const filteredDays = useMemo(() => {
    if (!trackerData?.days) return [];
    let list = trackerData.days;

    // Filter by selected chart day if one was clicked
    if (selectedChartDay !== null) {
      list = list.filter(d => d.day === selectedChartDay);
    } else if (dayViewFilter === 'activeOnly') {
      list = list.filter(d => d.totalBusiness > 0 || d.ordersCount > 0);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(d => {
        const dateMatch = d.date.includes(q) || d.dayName.toLowerCase().includes(q) || String(d.day) === q;
        const orderMatch = (d.orders || []).some(o => 
          (o.buyerName && o.buyerName.toLowerCase().includes(q)) ||
          (o.buyerSponsorCode && o.buyerSponsorCode.toLowerCase().includes(q)) ||
          (o.orderNumber && o.orderNumber.toLowerCase().includes(q)) ||
          (o.category && o.category.toLowerCase().includes(q))
        );
        return dateMatch || orderMatch;
      });
    }

    return list;
  }, [trackerData, dayViewFilter, selectedChartDay, searchQuery]);

  // Max day amount for visual bar scaling
  const maxDayBusiness = useMemo(() => {
    if (!trackerData?.days || trackerData.days.length === 0) return 1;
    const max = Math.max(...trackerData.days.map(d => d.totalBusiness));
    return max > 0 ? max : 1;
  }, [trackerData]);

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const DownlineTreeComponent = ({ tree, level = 0 }) => {
    return (
      <div>
        {tree.map((member) => {
          const isExpanded = expandedNodes.has(member._id);
          const hasChildren = member.children && member.children.length > 0;
          
          return (
            <div key={member._id} className="mb-2" style={{ marginLeft: `${level * 24}px` }}>
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                {/* Connection Line */}
                {level > 0 && (
                  <div className="absolute w-0.5 h-8 bg-gray-300" style={{ marginLeft: '-12px', marginTop: '-24px' }}></div>
                )}
                
                {/* Expand/Collapse Button */}
                {hasChildren && (
                  <button
                    onClick={() => toggleNode(member._id)}
                    className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 transition-colors flex-shrink-0 mt-1"
                  >
                    <span className="text-xs">{isExpanded ? '−' : '+'}</span>
                  </button>
                )}
                
                {/* Member Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">{member.name}</span>
                    <span className="text-xs text-gray-500 font-mono">{member.mySponsorCode}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      member.selfBusiness > 0 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {member.selfBusiness > 0 ? 'Active' : 'Inactive'}
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      Level {member.level}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-1">
                    {member.email}
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="font-medium">Rank: {member.rank || 'None'}</span>
                    <span>Self Business: ₹{(member.selfBusiness || 0).toLocaleString()}</span>
                    <span>Joined: {new Date(member.createdAt).toLocaleDateString()}</span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-blue-600 mt-1">
                    <span className="font-medium">
                      {member.directCount} direct referral{member.directCount !== 1 ? 's' : ''}
                    </span>
                    <span>
                      {member.totalTeamCount} total team member{member.totalTeamCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Children */}
              {hasChildren && isExpanded && (
                <div className="mt-2">
                  <DownlineTreeComponent tree={member.children} level={level + 1} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const teamTypeMeta = {
    sponsorTeam: { label: 'Sponsor Team', color: 'from-indigo-500 to-indigo-600', text: 'text-indigo-100' },
    boosterTeam: { label: 'Booster Team', color: 'from-emerald-500 to-emerald-600', text: 'text-emerald-100' },
    levelTeam: { label: 'Level Team', color: 'from-violet-500 to-violet-600', text: 'text-violet-100' },
    realTimeBonanzaTeam: { label: 'Real Time Bonanza Team', color: 'from-cyan-500 to-cyan-600', text: 'text-cyan-100' },
    lifeTimeRewardTeam: { label: 'Life Time Reward Team', color: 'from-pink-500 to-pink-600', text: 'text-pink-100' },
    allTeam: { label: 'All Team', color: 'from-orange-500 to-orange-600', text: 'text-orange-100' }
  };

  const selectedTeamMembers = useMemo(() => {
    if (!downlineData) return [];

    if (activeTeamType === 'allTeam') {
      return downlineData.teamMembers || [];
    }

    if (activeTeamType === 'sponsorTeam') {
      return downlineData.directSponsees || [];
    }

    const selectedIds = new Set(downlineData?.stats?.teamTypeMembers?.[activeTeamType] || []);
    return (downlineData.teamMembers || []).filter(member => selectedIds.has(String(member._id)));
  }, [downlineData, activeTeamType]);

  if (loading && !downlineData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E4572E] border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (error && !downlineData) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-600 mb-2">⚠️ {error}</div>
          <button
            onClick={fetchDownline}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0F2F3D] mb-1">My Team & Business</h1>
            <p className="text-gray-600">
              Track date-wise monthly business volume, downline hierarchy, and team performance
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                fetchDownline();
                fetchMonthlyTracker();
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm transition"
            >
              <span>🔄</span> Refresh All
            </button>
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* 🚀 MONTHLY BUSINESS TRACKER (DATE-WISE) SECTION AT TOP    */}
      {/* ======================================================== */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Tracker Header & Controls Bar */}
        <div className="p-5 md:p-6 bg-gradient-to-r from-[#0F2F3D] to-[#1E4D60] text-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#E4572E] text-white">
                  Monthly Tracker
                </span>
                <span className="text-xs text-gray-300">
                  Date-Wise Business Volume
                </span>
              </div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <span>📅</span>
                <span>{MONTH_NAMES[trackerMonth - 1]} {trackerYear} Business Tracker</span>
              </h2>
            </div>

            {/* Month & Year Selectors & Quick Nav */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handlePrevMonth}
                title="Previous Month"
                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition backdrop-blur-sm"
              >
                ← Prev
              </button>

              <select
                value={trackerMonth}
                onChange={(e) => {
                  setSelectedChartDay(null);
                  setTrackerMonth(Number(e.target.value));
                }}
                className="bg-white text-gray-800 font-semibold px-3 py-1.5 rounded-lg text-sm border-none shadow-sm focus:ring-2 focus:ring-[#E4572E]"
              >
                {MONTH_NAMES.map((name, idx) => (
                  <option key={idx + 1} value={idx + 1}>
                    {name}
                  </option>
                ))}
              </select>

              <select
                value={trackerYear}
                onChange={(e) => {
                  setSelectedChartDay(null);
                  setTrackerYear(Number(e.target.value));
                }}
                className="bg-white text-gray-800 font-semibold px-3 py-1.5 rounded-lg text-sm border-none shadow-sm focus:ring-2 focus:ring-[#E4572E]"
              >
                {availableYears.map((yr) => (
                  <option key={yr} value={yr}>
                    {yr}
                  </option>
                ))}
              </select>

              <button
                onClick={handleNextMonth}
                title="Next Month"
                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition backdrop-blur-sm"
              >
                Next →
              </button>

              <button
                onClick={handleCurrentMonth}
                title="Jump to Current Month"
                className="px-3 py-1.5 bg-[#E4572E] hover:bg-[#d04a23] text-white rounded-lg text-sm font-semibold shadow-sm transition"
              >
                This Month
              </button>
            </div>
          </div>

          {/* Scope Filters & View Controls */}
          <div className="mt-5 pt-4 border-t border-white/15 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            {/* Scope Filter Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-gray-300 font-medium mr-1">Scope:</span>
              {[
                { id: 'all', label: 'All Business' },
                { id: 'team', label: 'Total Team' },
                { id: 'direct', label: 'Direct Referrals' },
                { id: 'downline', label: 'Downline Team' },
                { id: 'self', label: 'Self Purchase' }
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => {
                    setTrackerType(filter.id);
                    setSelectedChartDay(null);
                  }}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
                    trackerType === filter.id
                      ? 'bg-white text-[#0F2F3D] shadow-sm'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* View Filter (Active only vs All days) */}
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-300 font-medium">Show:</span>
              <button
                onClick={() => setDayViewFilter('activeOnly')}
                className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                  dayViewFilter === 'activeOnly'
                    ? 'bg-[#E4572E] text-white'
                    : 'bg-white/10 text-gray-200 hover:bg-white/20'
                }`}
              >
                Active Days Only
              </button>
              <button
                onClick={() => setDayViewFilter('all')}
                className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                  dayViewFilter === 'all'
                    ? 'bg-[#E4572E] text-white'
                    : 'bg-white/10 text-gray-200 hover:bg-white/20'
                }`}
              >
                All Days (1-{trackerData?.daysInMonth || 30})
              </button>
            </div>
          </div>
        </div>

        {/* Tracker Body */}
        <div className="p-5 md:p-6 bg-gray-50/50 space-y-6">
          {trackerLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#E4572E] border-t-transparent mb-3"></div>
              <p className="text-sm text-gray-500 font-medium">
                Loading {MONTH_NAMES[trackerMonth - 1]} {trackerYear} business data...
              </p>
            </div>
          ) : trackerError ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
              <p className="text-red-600 font-medium text-sm mb-2">⚠️ {trackerError}</p>
              <button
                onClick={fetchMonthlyTracker}
                className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : trackerData ? (
            <>
              {/* Tracker KPI Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {/* Total Business */}
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-3.5 shadow-sm">
                  <div className="text-[11px] uppercase tracking-wider text-blue-200 font-medium">Total Business</div>
                  <div className="text-sm md:text-base font-bold mt-0.5">
                    ₹{(trackerData.summary.totalMonthlyBusiness || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-blue-100 mt-1 flex items-center gap-1">
                    <span>📦 {trackerData.summary.totalOrders} Orders</span>
                  </div>
                </div>

                {/* Direct Referrals Business */}
                <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-xl p-3.5 shadow-sm">
                  <div className="text-[11px] uppercase tracking-wider text-indigo-200 font-medium">Direct Team</div>
                  <div className="text-sm md:text-base font-bold mt-0.5">
                    ₹{(trackerData.summary.directMonthlyBusiness || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-indigo-100 mt-1">
                    1st Level Directs
                  </div>
                </div>

                {/* Downline Business */}
                <div className="bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-xl p-3.5 shadow-sm">
                  <div className="text-[11px] uppercase tracking-wider text-purple-200 font-medium">Downline Team</div>
                  <div className="text-sm md:text-base font-bold mt-0.5">
                    ₹{(trackerData.summary.downlineMonthlyBusiness || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-purple-100 mt-1">
                    Level 2+ Members
                  </div>
                </div>

                {/* Self Business */}
                <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-xl p-3.5 shadow-sm">
                  <div className="text-[11px] uppercase tracking-wider text-emerald-200 font-medium">Self Business</div>
                  <div className="text-sm md:text-base font-bold mt-0.5">
                    ₹{(trackerData.summary.selfMonthlyBusiness || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-emerald-100 mt-1">
                    Personal Purchases
                  </div>
                </div>

                {/* Active Buyers */}
                <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-xl p-3.5 shadow-sm">
                  <div className="text-[11px] uppercase tracking-wider text-orange-100 font-medium">Active Buyers</div>
                  <div className="text-sm md:text-base font-bold mt-0.5">
                    {trackerData.summary.activeBuyersCount || 0}
                  </div>
                  <div className="text-[10px] text-orange-100 mt-1">
                    Unique Members
                  </div>
                </div>

                {/* Peak Day / Average */}
                <div className="bg-gradient-to-br from-slate-700 to-[#0F2F3D] text-white rounded-xl p-3.5 shadow-sm">
                  <div className="text-[11px] uppercase tracking-wider text-gray-300 font-medium">Peak Day</div>
                  <div className="text-xs md:text-sm font-bold mt-0.5 truncate">
                    {trackerData.summary.highestDay?.amount > 0 ? (
                      `Day ${trackerData.summary.highestDay.day}: ₹${trackerData.summary.highestDay.amount.toLocaleString()}`
                    ) : (
                      'No orders yet'
                    )}
                  </div>
                  <div className="text-[10px] text-gray-300 mt-1 truncate">
                    Avg ₹{(trackerData.summary.averageDailyBusiness || 0).toLocaleString()}/day
                  </div>
                </div>
              </div>

              {/* Visual Daily Timeline / Bar Visualizer */}
              <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                      <span>📊</span> Daily Business Volume Chart
                    </h3>
                    <p className="text-xs text-gray-500">
                      Click any day to quickly filter date-wise breakdown below
                    </p>
                  </div>
                  {selectedChartDay !== null && (
                    <button
                      onClick={() => setSelectedChartDay(null)}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition"
                    >
                      <span>✕</span> Clear Filter (Day {selectedChartDay})
                    </button>
                  )}
                </div>

                {/* Day Columns */}
                <div className="overflow-x-auto pb-2">
                  <div className="min-w-[680px] flex items-end gap-1.5 h-36 pt-4 border-b border-gray-200">
                    {trackerData.days.map((d) => {
                      const heightPercent = d.totalBusiness > 0
                        ? Math.max(Math.round((d.totalBusiness / maxDayBusiness) * 100), 12)
                        : 6;
                      const isSelected = selectedChartDay === d.day;
                      const hasBusiness = d.totalBusiness > 0;

                      return (
                        <div
                          key={d.day}
                          onClick={() => {
                            setSelectedChartDay(prev => prev === d.day ? null : d.day);
                          }}
                          className={`flex-1 flex flex-col items-center justify-end cursor-pointer group relative transition-all`}
                        >
                          {/* Tooltip on hover */}
                          <div className="opacity-0 group-hover:opacity-100 pointer-events-none absolute -top-10 z-20 bg-gray-900 text-white text-[10px] rounded px-2 py-1 whitespace-nowrap shadow-md transition-opacity">
                            {d.date} ({d.dayName}): ₹{d.totalBusiness.toLocaleString()} ({d.ordersCount} orders)
                          </div>

                          {/* Bar */}
                          <div
                            style={{ height: `${heightPercent}%` }}
                            className={`w-full rounded-t-sm transition-all duration-200 ${
                              isSelected
                                ? 'bg-[#E4572E] ring-2 ring-offset-1 ring-[#E4572E]'
                                : hasBusiness
                                ? 'bg-gradient-to-t from-teal-600 to-teal-400 group-hover:from-orange-500 group-hover:to-orange-400'
                                : 'bg-gray-200 group-hover:bg-gray-300'
                            }`}
                          ></div>

                          {/* Day Label */}
                          <div className={`text-[10px] mt-1.5 font-medium ${
                            isSelected ? 'text-[#E4572E] font-bold' : hasBusiness ? 'text-gray-800' : 'text-gray-400'
                          }`}>
                            {d.day}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between text-[11px] text-gray-500 mt-2 px-1">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm bg-teal-500"></span> Active Business
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#E4572E]"></span> Selected Day
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm bg-gray-200"></span> Zero Business
                    </span>
                  </div>
                  <div>
                    Peak Day Volume: <span className="font-semibold text-gray-800">₹{maxDayBusiness.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Date-Wise Breakdown Table */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Search & Actions Bar */}
                <div className="p-4 bg-gray-50/70 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-bold text-gray-900">
                      Date-Wise Business Breakdown ({filteredDays.length} {filteredDays.length === 1 ? 'Day' : 'Days'})
                    </h3>
                    {selectedChartDay !== null && (
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-800">
                        Filtered: Day {selectedChartDay}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* Search box */}
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search buyer, code, date..."
                        className="w-56 md:w-64 pl-8 pr-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E4572E] focus:outline-none"
                      />
                      <span className="absolute left-2.5 top-2 text-gray-400 text-xs">🔍</span>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2.5 top-1.5 text-gray-400 hover:text-gray-600 text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {/* Expand/Collapse All */}
                    {filteredDays.length > 0 && (
                      <button
                        onClick={() => toggleAllExpanded(filteredDays)}
                        className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-medium transition"
                      >
                        {filteredDays.every(d => expandedTrackerDays.has(d.day)) ? 'Collapse All' : 'Expand All'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Table Content */}
                {filteredDays.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <div className="text-4xl mb-2">📭</div>
                    <div className="font-semibold text-gray-700 text-sm">No business records found</div>
                    <p className="text-xs text-gray-500 mt-1">
                      {searchQuery
                        ? 'Try clearing your search query.'
                        : dayViewFilter === 'activeOnly'
                        ? 'No purchases on active days. Switch to "All Days" to see the full calendar.'
                        : 'No orders recorded for this month with the selected filter.'}
                    </p>
                    <div className="mt-4 flex justify-center gap-2">
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-300 font-medium"
                        >
                          Clear Search
                        </button>
                      )}
                      {dayViewFilter === 'activeOnly' && (
                        <button
                          onClick={() => setDayViewFilter('all')}
                          className="px-3 py-1.5 bg-[#E4572E] text-white text-xs rounded-lg hover:bg-[#d04a23] font-medium"
                        >
                          Show All Days
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 text-gray-700 text-xs uppercase tracking-wider font-semibold border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3">Day / Date</th>
                          <th className="px-4 py-3">Total Business</th>
                          <th className="px-4 py-3">Direct Referrals</th>
                          <th className="px-4 py-3">Downline Team</th>
                          <th className="px-4 py-3">Self Business</th>
                          <th className="px-4 py-3 text-center">Orders</th>
                          <th className="px-4 py-3 text-right">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredDays.map((dayItem) => {
                          const isExpanded = expandedTrackerDays.has(dayItem.day);
                          const hasOrders = dayItem.ordersCount > 0;

                          return (
                            <React.Fragment key={dayItem.day}>
                              <tr
                                className={`transition-colors ${
                                  hasOrders ? 'hover:bg-orange-50/40' : 'bg-gray-50/30 text-gray-400 hover:bg-gray-100/50'
                                }`}
                              >
                                <td className="px-4 py-3 font-medium">
                                  <div className="flex items-center gap-2">
                                    <span className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center font-bold text-gray-800 text-xs">
                                      {dayItem.day}
                                    </span>
                                    <div>
                                      <div className="text-gray-900 font-semibold text-xs">{dayItem.date}</div>
                                      <div className="text-[11px] text-gray-500">{dayItem.dayName}</div>
                                    </div>
                                  </div>
                                </td>

                                <td className="px-4 py-3 text-xs">
                                  <span className={`text-xs font-semibold ${dayItem.totalBusiness > 0 ? 'text-[#0F2F3D]' : 'text-gray-400'}`}>
                                    ₹{dayItem.totalBusiness.toLocaleString()}
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-xs">
                                  <span className={dayItem.directBusiness > 0 ? 'text-indigo-600 font-medium' : 'text-gray-400'}>
                                    ₹{dayItem.directBusiness.toLocaleString()}
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-xs">
                                  <span className={dayItem.downlineBusiness > 0 ? 'text-purple-600 font-medium' : 'text-gray-400'}>
                                    ₹{dayItem.downlineBusiness.toLocaleString()}
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-xs">
                                  <span className={dayItem.selfBusiness > 0 ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                                    ₹{dayItem.selfBusiness.toLocaleString()}
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                    hasOrders ? 'bg-orange-100 text-[#E4572E]' : 'bg-gray-100 text-gray-400'
                                  }`}>
                                    {dayItem.ordersCount}
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-right">
                                  {hasOrders ? (
                                    <button
                                      onClick={() => toggleTrackerDay(dayItem.day)}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
                                    >
                                      <span>{isExpanded ? 'Hide' : 'View'}</span>
                                      <span className="text-[10px]">{isExpanded ? '▲' : '▼'}</span>
                                    </button>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </td>
                              </tr>

                              {/* Expanded Orders Drawer for this Day */}
                              {isExpanded && hasOrders && (
                                <tr className="bg-orange-50/30">
                                  <td colSpan={7} className="p-4">
                                    <div className="bg-white rounded-lg border border-orange-200/70 p-4 shadow-inner">
                                      <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                                          <span>📦</span> Orders on {dayItem.date} ({dayItem.orders.length} Purchases)
                                        </h4>
                                        <span className="text-xs font-bold text-[#E4572E]">
                                          Day Total: ₹{dayItem.totalBusiness.toLocaleString()}
                                        </span>
                                      </div>

                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                          <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                                            <tr>
                                              <th className="px-3 py-2 text-left">Order #</th>
                                              <th className="px-3 py-2 text-left">Buyer Name</th>
                                              <th className="px-3 py-2 text-left">Sponsor Code</th>
                                              <th className="px-3 py-2 text-left">Scope / Category</th>
                                              <th className="px-3 py-2 text-left">Status</th>
                                              <th className="px-3 py-2 text-right">Business Volume</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-100">
                                            {dayItem.orders.map((order, orderIdx) => {
                                              let catColor = 'bg-gray-100 text-gray-700';
                                              if (order.category === 'SELF') catColor = 'bg-emerald-100 text-emerald-800 border border-emerald-300';
                                              else if (order.category === 'DIRECT') catColor = 'bg-indigo-100 text-indigo-800 border border-indigo-300';
                                              else if (order.category === 'DOWNLINE') catColor = 'bg-purple-100 text-purple-800 border border-purple-300';

                                              return (
                                                <tr key={order.orderId || orderIdx} className="hover:bg-gray-50/80">
                                                  <td className="px-3 py-2 font-mono text-gray-700">
                                                    {order.orderNumber || String(order.orderId).slice(-6)}
                                                  </td>
                                                  <td className="px-3 py-2 font-medium text-gray-900">
                                                    {order.buyerName}
                                                  </td>
                                                  <td className="px-3 py-2 font-mono text-blue-600">
                                                    {order.buyerSponsorCode}
                                                  </td>
                                                  <td className="px-3 py-2">
                                                    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${catColor}`}>
                                                      {order.category}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2">
                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
                                                      {order.status}
                                                    </span>
                                                  </td>
                                                  <td className="px-3 py-2 text-right font-bold text-gray-900">
                                                    ₹{order.amount.toLocaleString()}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* ======================================================== */}
      {/* 👥 EXISTING DOWNLINE STATISTICS CARDS                    */}
      {/* ======================================================== */}
      {downlineData?.stats && (
        <div>
          <h2 className="text-xl font-bold text-[#0F2F3D] mb-4">Downline Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold mb-1">{downlineData.stats.totalDirect}</div>
                  <div className="text-blue-100">Direct Referrals</div>
                </div>
                <div className="text-4xl opacity-50">👥</div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold mb-1">{downlineData.stats.totalTeam}</div>
                  <div className="text-green-100">Total Team Size</div>
                </div>
                <div className="text-4xl opacity-50">🌳</div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold mb-1">{downlineData.stats.totalActive}</div>
                  <div className="text-purple-100">Active Members</div>
                </div>
                <div className="text-4xl opacity-50">✨</div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold mb-1">{downlineData.stats.totalInactive}</div>
                  <div className="text-orange-100">Inactive Members</div>
                </div>
                <div className="text-4xl opacity-50">💤</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 🏷️ EXISTING TEAM TYPE CARDS                              */}
      {/* ======================================================== */}
      {downlineData?.stats?.teamTypes && (
        <div>
          <h2 className="text-xl font-bold text-[#0F2F3D] mb-4">Team Categories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(teamTypeMeta).map(([key, meta]) => (
              <button
                key={key}
                onClick={() => setActiveTeamType(key)}
                className={`bg-gradient-to-r ${meta.color} rounded-xl p-5 text-white text-left transition-all ${
                  activeTeamType === key ? 'ring-4 ring-offset-2 ring-blue-300 scale-[1.02]' : 'hover:opacity-95 shadow-sm'
                }`}
              >
                <div className="text-2xl font-bold">{downlineData.stats.teamTypes[key] || 0}</div>
                <div className={`${meta.text} text-sm font-medium`}>{meta.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 📋 EXISTING SELECTED TEAM MEMBERS TABLE                  */}
      {/* ======================================================== */}
      {downlineData?.stats?.teamTypes && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {teamTypeMeta[activeTeamType]?.label || 'Team'} Members ({selectedTeamMembers.length})
          </h3>

          {selectedTeamMembers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Sr. No</th>
                    <th className="px-4 py-3 text-left font-semibold">Name</th>
                    <th className="px-4 py-3 text-left font-semibold">Sponsor Code</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Rank</th>
                    <th className="px-4 py-3 text-left font-semibold">Business</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {selectedTeamMembers.map((member, index) => (
                    <tr key={member._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{index + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{member.name}</td>
                      <td className="px-4 py-3 font-mono text-blue-600">{member.mySponsorCode}</td>
                      <td className="px-4 py-3 text-gray-600">{member.email}</td>
                      <td className="px-4 py-3 text-gray-700">{member.rank || 'NONE'}</td>
                      <td className="px-4 py-3 text-gray-700">₹{(member.selfBusiness || 0).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          member.selfBusiness > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {member.selfBusiness > 0 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-gray-500">No members found in this team category.</div>
          )}
        </div>
      )}

      {/* ======================================================== */}
      {/* 🎖️ EXISTING RANK DISTRIBUTION                            */}
      {/* ======================================================== */}
      {downlineData?.stats?.ranks && Object.keys(downlineData.stats.ranks).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Rank Distribution</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(downlineData.stats.ranks).map(([rank, count]) => (
              <div key={rank} className="px-4 py-2 bg-gray-100 rounded-lg flex items-center gap-2 border border-gray-200">
                <span className="font-medium text-gray-900">{rank}</span>
                <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs font-semibold">
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 🌲 EXISTING TEAM STRUCTURE (TREE VIEW)                   */}
      {/* ======================================================== */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Team Structure Tree</h3>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Click + to expand</span>
            <span>•</span>
            <button
              onClick={fetchDownline}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Refresh Tree
            </button>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto border border-gray-200">
          {downlineData?.downlineTree && downlineData.downlineTree.length > 0 ? (
            <DownlineTreeComponent tree={downlineData.downlineTree} />
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🌱</div>
              <h4 className="text-lg font-semibold text-gray-700 mb-2">Start Building Your Team</h4>
              <p className="text-gray-500 mb-4">You don't have any team members yet.</p>
              <p className="text-sm text-gray-400">
                Share your sponsor code <span className="font-mono bg-gray-200 px-2 py-1 rounded">{user?.mySponsorCode}</span> to start building your network!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ======================================================== */}
      {/* 💡 EXISTING TEAM BUILDING TIPS                           */}
      {/* ======================================================== */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h4 className="font-semibold text-blue-900 mb-3">💡 Team Building Tips</h4>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>• Share your sponsor code with friends and family to grow your direct referrals</li>
          <li>• Help your team members become active to increase your team business volume</li>
          <li>• Regular training and support helps your team grow faster</li>
          <li>• Higher ranks unlock more earning opportunities</li>
        </ul>
      </div>
    </div>
  );
};

export default UserDownline;
